import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import {
    concatMap,
    switchMap,
    map,
    catchError,
    withLatestFrom,
    filter
} from 'rxjs/operators';

import { Store, select } from '@ngrx/store';
import { ReminderService } from '../reminder.service';
import * as ReminderActions from './reminder.actions';
import * as ReminderReducer from './reminder.reducer';
import * as ReminderSelectors from './reminder.selectors';
import * as fromShared from '../../shared';
import { ResponseStatus, DisplayMessageMinimum } from '../../shared';

@Injectable()
export class ReminderEffects {
    getReminders$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReminderActions.getReminders),
            switchMap(() =>
                this.service.get().pipe(
                    map(resp =>
                        ReminderActions.loadReminders({ reminders: resp })
                    ),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
    getReminderTypes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ReminderActions.getReminderTypes),
            withLatestFrom(
                this.store.pipe(select(ReminderSelectors.selectReminderTypes))
            ),
            filter(([, items]) => items.length === 0),
            switchMap(() =>
                this.service.getReminderTypes().pipe(
                    map(data =>
                        ReminderActions.loadReminderTypes({
                            reminderTypes: data
                        })
                    ),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        );
    });
    getReminderScheduleTypes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ReminderActions.getReminderScheduleTypes),
            withLatestFrom(
                this.store.pipe(
                    select(ReminderSelectors.selectReminderScheduleTypes)
                )
            ),
            filter(([, items]) => items.length === 0),
            switchMap(() =>
                this.service.getReminderScheduleTypes().pipe(
                    map(data =>
                        ReminderActions.loadReminderScheduleTypes({
                            reminderScheduleTypes: data
                        })
                    ),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        );
    });
    getReminderRecipients$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ReminderActions.getReminderRecipients),
            withLatestFrom(
                this.store.pipe(
                    select(ReminderSelectors.selectReminderRecipients)
                )
            ),
            filter(([, items]) => items.length === 0),
            switchMap(() =>
                this.service.getReminderRecipients().pipe(
                    map(data =>
                        ReminderActions.loadReminderRecipients({
                            recipients: data
                        })
                    ),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        );
    });
    getReminderReviewPeriods$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ReminderActions.getReminderReviewPeriods),
            withLatestFrom(
                this.store.pipe(
                    select(ReminderSelectors.selectReminderReviewPeriods)
                )
            ),
            filter(([, items]) => items.length === 0),
            switchMap(() =>
                this.service.getReminderReviewPeriods().pipe(
                    map(data =>
                        ReminderActions.loadReminderReviewPeriods({
                            reminderReviewPeriods: data
                        })
                    ),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        );
    });
    putRemindersStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReminderActions.putReminders),
            map(a =>
                ReminderActions.updateReminders({
                    reminders: a.reminders.map(x => ({
                        id: x.Remindertypeid,
                        changes: {
                            Status: {
                                StatusCode: ResponseStatus.Loading,
                                Message: 'Loading'
                            }
                        }
                    }))
                })
            )
        )
    );
    putReminders$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReminderActions.putReminders),
            switchMap(a =>
                this.service.put(a.reminders).pipe(
                    concatMap(resp =>
                        fromShared.Errors.IsSuccessStatus(resp.StatusCode)
                            ? [
                                  ReminderActions.putRemindersSuccess({
                                      reminders: resp.Items
                                  })
                              ]
                            : [
                                  new fromShared.Errors.SetErrors(
                                      fromShared.Errors.MapErrorsToStatus(resp)
                                  )
                              ]
                    ),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
    runReminders$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReminderActions.runReminder),
            switchMap(a =>
                this.service.post(a.Reminderinstanceid).pipe(
                    map(resp => resp[0]),
                    map(resp => ({
                        StatusCode: resp.Status.StatusCode,
                        ErrorMessages: [
                            resp.Status.StatusCode >= ResponseStatus.Failed
                                ? resp.Status.Message
                                : ''
                        ],
                        WarningMessages: [
                            resp.Status.StatusCode === ResponseStatus.Warning
                                ? resp.Status.Message
                                : ''
                        ],
                        Items: [resp],
                        StatusType: null
                    })),
                    concatMap(resp =>
                        fromShared.Errors.IsSuccessStatus(resp.StatusCode)
                            ? [
                                  ReminderActions.putRemindersSuccess({
                                      reminders: resp.Items
                                  })
                              ]
                            : [
                                  new fromShared.Errors.SetErrors(
                                      fromShared.Errors.MapErrorsToStatus(resp)
                                  )
                              ]
                    ),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );

    putReminderSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ReminderActions.putRemindersSuccess),
            map(action =>
                ReminderActions.upsertReminders({
                    reminders: action.reminders
                })
            )
        );
    });
    removeReminder$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ReminderActions.removeReminder),
            switchMap(action =>
                this.service.delete(action.id).pipe(
                    concatMap(resp =>
                        fromShared.Errors.IsSuccessStatus(resp.StatusCode)
                            ? [
                                  ReminderActions.deleteReminder({
                                      id: action.id
                                  })
                              ]
                            : [
                                  new fromShared.Errors.SetErrors(
                                      fromShared.Errors.MapErrorsToStatus({
                                          ...resp,
                                          id: action.id
                                      })
                                  ),
                                  ReminderActions.updateReminder({
                                      reminder: {
                                          id: action.id,
                                          changes: {
                                              Status: {
                                                  StatusCode: resp.StatusCode,
                                                  Message:
                                                      resp.ErrorMessages.reduce(
                                                          (a, c) =>
                                                              `${a}\n${c}`,
                                                          ''
                                                      )
                                              }
                                          }
                                      }
                                  })
                              ]
                    ),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        );
    });

    constructor(
        private actions$: Actions,
        private service: ReminderService,
        private store: Store<ReminderReducer.State>
    ) {}
}
